import React, { useState, useEffect, useCallback } from 'react'
import {
  CalendarDaysIcon,
  ChartBarIcon,
  ClockIcon,
  Cog6ToothIcon,
  BellIcon,
  ArrowRightOnRectangleIcon,
  ShieldCheckIcon,
  SunIcon,
  MoonIcon
} from '@heroicons/react/24/outline'
import Dashboard from './pages/Dashboard'
import Calendar from './pages/Calendar'
import Reports from './pages/Reports'
import Settings from './pages/Settings'
import Notifications from './pages/Notifications'
import AdminPanel from './pages/AdminPanel'
import Login from './pages/Login'
import { useAuth } from './contexts/AuthContext'
import { useTheme } from './contexts/ThemeContext'
import { useNotificationPolling } from './hooks/useNotificationPolling'

type Tab = 'dashboard' | 'calendar' | 'reports' | 'notifications' | 'settings' | 'admin'

const TABS: Tab[] = ['dashboard', 'calendar', 'reports', 'notifications', 'settings', 'admin']

// Keeps the visible tab in the URL so a refresh / deep link does not fall back to the dashboard.
const readTabFromUrl = (): Tab => {
  const hash = window.location.hash.replace(/^#\/?/, '').split('?')[0]
  if ((TABS as string[]).includes(hash)) return hash as Tab
  const path = window.location.pathname.replace(/^\/+|\/+$/g, '')
  if ((TABS as string[]).includes(path)) return path as Tab
  return 'dashboard'
}

export const App: React.FC = () => {
  const { user, isAuthenticated, isLoading, isAdmin, logout } = useAuth()
  const { theme, toggleTheme } = useTheme()
  const [activeTab, setActiveTabState] = useState<Tab>(readTabFromUrl)

  const setActiveTab = useCallback((tab: Tab) => {
    setActiveTabState(tab)
    if (window.location.hash !== `#/${tab}`) {
      window.history.pushState(null, '', `#/${tab}`)
    }
  }, [])

  // Browser back / forward and manual hash edits keep working.
  useEffect(() => {
    const sync = () => setActiveTabState(readTabFromUrl())
    window.addEventListener('hashchange', sync)
    window.addEventListener('popstate', sync)
    return () => {
      window.removeEventListener('hashchange', sync)
      window.removeEventListener('popstate', sync)
    }
  }, [])

  // Normalise path deep links (/calendar) into the hash form the app actually reads.
  useEffect(() => {
    if (!isAuthenticated) return
    if (window.location.hash !== `#/${activeTab}` || window.location.pathname !== '/') {
      window.history.replaceState(null, '', `/#/${activeTab}`)
    }
  }, [isAuthenticated, activeTab])

  // A non-admin must never sit on the admin tab, even via a stale URL.
  useEffect(() => {
    if (!isLoading && isAuthenticated && activeTab === 'admin' && !isAdmin) {
      setActiveTab('dashboard')
    }
  }, [isLoading, isAuthenticated, activeTab, isAdmin, setActiveTab])
  const { unreadCount } = useNotificationPolling()

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-900">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-emerald-600"></div>
      </div>
    )
  }

  if (!isAuthenticated) {
    return <Login />
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 text-slate-800 dark:text-slate-100 flex flex-col justify-between transition-colors duration-200">
      {/* Top Navbar */}
      <header className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700/60 sticky top-0 z-30 shadow-xs transition-colors duration-200">
        <div className="max-w-5xl mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-emerald-600 flex items-center justify-center text-white font-black shadow-md shadow-emerald-500/20">
              ح
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="text-sm font-black text-slate-800 dark:text-white">
                  {user?.name}
                </span>
                {isAdmin && (
                  <span className="px-2 py-0.5 rounded-md text-[10px] font-extrabold bg-purple-100 dark:bg-purple-950 text-purple-700 dark:text-purple-300">
                    مدیر سیستم
                  </span>
                )}
              </div>
              <p className="text-[10px] text-slate-400">
                سیستم ثبت تردد و کارکرد
              </p>
            </div>
          </div>

          <div className="flex items-center gap-1.5 sm:gap-2">
            {/* Theme Toggle Button */}
            <button
              onClick={toggleTheme}
              className="p-2 text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-700 transition"
              title={theme === 'dark' ? 'حالت روز' : 'حالت شب'}
            >
              {theme === 'dark' ? (
                <SunIcon className="w-5 h-5 text-amber-400" />
              ) : (
                <MoonIcon className="w-5 h-5 text-slate-600" />
              )}
            </button>

            {/* Notifications Button */}
            <button
              onClick={() => setActiveTab('notifications')}
              className="relative p-2 text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-700 transition"
              title="اعلان‌ها"
            >
              <BellIcon className="w-5 h-5" />
              {unreadCount > 0 && (
                <span className="absolute top-1 right-1 w-2.5 h-2.5 bg-rose-500 rounded-full ring-2 ring-white dark:ring-slate-800 animate-pulse"></span>
              )}
            </button>

            {/* Logout Button */}
            <button
              onClick={logout}
              className="p-2 text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-950/40 rounded-xl transition"
              title="خروج از حساب"
            >
              <ArrowRightOnRectangleIcon className="w-5 h-5" />
            </button>
          </div>
        </div>
      </header>

      {/* Main Page Content */}
      <main className="max-w-5xl mx-auto px-4 py-6 flex-1 w-full">
        {activeTab === 'dashboard' && <Dashboard />}
        {activeTab === 'calendar' && <Calendar />}
        {activeTab === 'reports' && <Reports />}
        {activeTab === 'notifications' && <Notifications />}
        {activeTab === 'settings' && <Settings />}
        {activeTab === 'admin' && isAdmin && <AdminPanel />}
      </main>

      {/* Bottom Navigation Bar */}
      <nav className="fixed bottom-0 left-0 right-0 bg-white/90 dark:bg-slate-800/90 backdrop-blur-md border-t border-slate-200 dark:border-slate-700/60 z-30 pb-safe transition-colors duration-200">
        <div className="max-w-md mx-auto flex items-center justify-around py-2">
          <button
            onClick={() => setActiveTab('dashboard')}
            className={`flex flex-col items-center gap-1 p-2 rounded-2xl transition ${
              activeTab === 'dashboard'
                ? 'text-emerald-600 dark:text-emerald-400 font-bold'
                : 'text-slate-400 hover:text-slate-600 dark:hover:text-slate-300'
            }`}
          >
            <ClockIcon className="w-5 h-5" />
            <span className="text-[11px]">داشبورد</span>
          </button>

          <button
            onClick={() => setActiveTab('calendar')}
            className={`flex flex-col items-center gap-1 p-2 rounded-2xl transition ${
              activeTab === 'calendar'
                ? 'text-emerald-600 dark:text-emerald-400 font-bold'
                : 'text-slate-400 hover:text-slate-600 dark:hover:text-slate-300'
            }`}
          >
            <CalendarDaysIcon className="w-5 h-5" />
            <span className="text-[11px]">تقویم</span>
          </button>

          <button
            onClick={() => setActiveTab('reports')}
            className={`flex flex-col items-center gap-1 p-2 rounded-2xl transition ${
              activeTab === 'reports'
                ? 'text-emerald-600 dark:text-emerald-400 font-bold'
                : 'text-slate-400 hover:text-slate-600 dark:hover:text-slate-300'
            }`}
          >
            <ChartBarIcon className="w-5 h-5" />
            <span className="text-[11px]">گزارش‌ها</span>
          </button>

          {isAdmin && (
            <button
              onClick={() => setActiveTab('admin')}
              className={`flex flex-col items-center gap-1 p-2 rounded-2xl transition ${
                activeTab === 'admin'
                  ? 'text-purple-600 dark:text-purple-400 font-bold'
                  : 'text-slate-400 hover:text-slate-600 dark:hover:text-slate-300'
              }`}
            >
              <ShieldCheckIcon className="w-5 h-5" />
              <span className="text-[11px]">مدیریت</span>
            </button>
          )}

          <button
            onClick={() => setActiveTab('settings')}
            className={`flex flex-col items-center gap-1 p-2 rounded-2xl transition ${
              activeTab === 'settings'
                ? 'text-emerald-600 dark:text-emerald-400 font-bold'
                : 'text-slate-400 hover:text-slate-600 dark:hover:text-slate-300'
            }`}
          >
            <Cog6ToothIcon className="w-5 h-5" />
            <span className="text-[11px]">تنظیمات</span>
          </button>
        </div>
      </nav>
    </div>
  )
}

export default App
